import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/lib/supabase";
import { createFileRoute, useRouter, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

type SignupFormData = {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
};

export const Route = createFileRoute("/signup")({
  component: Signup,
});

function Signup() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = Route.useSearch() as {
    email?: string;
    token?: string;
    project?: string;
  };
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch,
    setValue,
  } = useForm<SignupFormData>();

  const password = watch("password");

  // Check if user came from invitation
  useEffect(() => {
    if (searchParams.token) {
      setIsInvitedUser(true);
      setInvitationToken(searchParams.token);
      setProjectName(searchParams.project || null);

      // Pre-fill email if provided
      if (searchParams.email) {
        setValue("email", searchParams.email);
      }
    }
  }, [searchParams, setValue]);

  const handleGoogleSignup = async () => {
    try {
      const result = await authService.signInWithOAuth({
        provider: "google",
        redirectTo: `${window.location.origin}/google-callback`,
      });

      if (result.error) {
        toast.error(
          t("signup.failedToInitiateGoogleSignup", {
            defaultValue: "Failed to initiate Google signup",
          }),
        );
      } else if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Google signup error:", error);
      toast.error(
        t("signup.googleSignupError", {
          defaultValue: "An error occurred during Google signup",
        }),
      );
    }
  };

  const onSubmit = async (data: SignupFormData) => {
    try {
      const result = await authService.signUp({
        email: data.email,
        password: data.password,
        metadata: {
          fullName: data.fullName,
          // Store if user came from invitation
          invited: isInvitedUser,
        },
      });

      if (result.error) {
        setError("root", {
          type: "manual",
          message: result.error.message,
        });
      } else {
        router.navigate({
          to: "/verify-email",
          search: { email: data.email },
        });
      }
    } catch {
      setError("root", {
        type: "manual",
        message: t("signup.unexpectedError", {
          defaultValue: "An unexpected error occurred",
        }),
      });
    }
  };

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey={"signup.title"}>Create an account</Trans>
              </CardTitle>
              <CardDescription>
                {isInvitedUser && projectName ? (
                  <Trans i18nKey={"signup.invitationDescription"}>
                    You have been invited to the{" "}
                    <strong>{{ projectName }}</strong> project.
                    <br />
                    Please enter your information below to create an account.
                  </Trans>
                ) : (
                  <Trans i18nKey={"signup.description"}>
                    Enter your information to create your account
                  </Trans>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="fullName">
                      <Trans i18nKey={"signup.name"}>Name</Trans>
                    </Label>
                    <Input
                      id="fullName"
                      placeholder={t("signup.namePlaceholder", {
                        defaultValue: "Doe",
                      })}
                      {...register("fullName", {
                        required: t("signup.nameRequired", {
                          defaultValue: "Name is required",
                        }),
                      })}
                    />
                    {errors.fullName && (
                      <p className="text-sm text-red-600">
                        {errors.fullName.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">
                      <Trans i18nKey={"signup.email"}>Email</Trans>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t("signup.emailPlaceholder", {
                        defaultValue: "m@example.com",
                      })}
                      {...register("email", {
                        required: t("signup.emailRequired", {
                          defaultValue: "Email is required",
                        }),
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: t("signup.invalidEmail", {
                            defaultValue: "Invalid email address",
                          }),
                        },
                      })}
                      disabled={isInvitedUser && !!searchParams.email}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="password">
                      <Trans i18nKey={"signup.password"}>Password</Trans>
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      {...register("password", {
                        required: t("signup.passwordRequired", {
                          defaultValue: "Password is required",
                        }),
                        minLength: {
                          value: 6,
                          message: t("signup.passwordMinLength", {
                            defaultValue:
                              "Password must be at least 6 characters",
                          }),
                        },
                      })}
                    />
                    {errors.password && (
                      <p className="text-sm text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="confirmPassword">
                      <Trans i18nKey={"signup.confirmPassword"}>
                        Confirm password
                      </Trans>
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      {...register("confirmPassword", {
                        required: t("signup.confirmPasswordRequired", {
                          defaultValue: "Please confirm your password",
                        }),
                        validate: (value) =>
                          value === password ||
                          t("signup.passwordsDoNotMatch", {
                            defaultValue: "Passwords do not match",
                          }),
                      })}
                    />
                    {errors.confirmPassword && (
                      <p className="text-sm text-red-600">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                  {errors.root && (
                    <p className="text-sm text-red-600">
                      {errors.root.message}
                    </p>
                  )}
                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t("signup.creatingAccount", {
                            defaultValue: "Creating account...",
                          })
                        : t("signup.createAccountButton", {
                            defaultValue: "Create account",
                          })}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          <Trans i18nKey={"signup.orContinueWith"}>
                            Or continue with
                          </Trans>
                        </span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleGoogleSignup}
                    >
                      <svg
                        className="mr-2 h-4 w-4"
                        aria-hidden="true"
                        focusable="false"
                        data-prefix="fab"
                        data-icon="google"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 488 512"
                      >
                        <path
                          fill="currentColor"
                          d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                        />
                      </svg>
                      <Trans i18nKey={"signup.continueWithGoogle"}>
                        Continue with Google
                      </Trans>
                    </Button>
                  </div>
                </div>
                <div className="mt-4 text-center text-sm">
                  <Trans i18nKey={"signup.alreadyHaveAccount"}>
                    Already have an account?
                    <Link to="/login" className="underline underline-offset-4">
                      Sign in
                    </Link>
                  </Trans>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="fixed top-6 left-5">
        <Button
          variant="link"
          onClick={() =>
            router.history.canGoBack()
              ? router.history.back()
              : router.navigate({ to: "/" })
          }
        >
          <ArrowLeft />
          <Trans i18nKey={"signup.goBack"}>Go back</Trans>
        </Button>
      </div>
    </div>
  );
}
